import type { PaginatedData } from '@solar/contracts';

import { apiClient } from '../../lib/api-client';

export interface AdminUser {
  id: string;
  email: string;
  userType: string;
  status: string;
  emailVerifiedAt: string | null;
  lastLoginAt: string | null;
  lockedUntil: string | null;
  failedLoginAttempts: number;
  createdAt: string;
  updatedAt: string;
  roles: { id: string; code: string; name: string }[];
}

export interface AdminRole {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isSystem: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
  permissionCodes: string[];
  assignedUserCount: number;
}

export interface AdminPermission {
  id: string;
  code: string;
  name: string;
  module: string;
  description: string | null;
}

export interface AuditLog {
  id: string;
  actorUserId: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  before: unknown;
  after: unknown;
  reason: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
  createdAt: string;
}

export interface UserListFilters {
  page?: number;
  pageSize?: number;
  email?: string;
  status?: string;
  userType?: string;
}

/** Builds a query string, omitting anything empty so filters stay optional. */
function toQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }

  const query = search.toString();

  return query === '' ? '' : `?${query}`;
}

export const adminApi = {
  listUsers(filters: UserListFilters): Promise<PaginatedData<AdminUser>> {
    return apiClient.get(`/admin/users${toQuery({ ...filters })}`);
  },

  getUser(id: string): Promise<AdminUser> {
    return apiClient.get(`/admin/users/${id}`);
  },

  createStaffUser(input: {
    email: string;
    roleCodes: string[];
    reason: string;
  }): Promise<AdminUser> {
    return apiClient.post('/admin/users', input);
  },

  updateUser(id: string, input: { status?: string; reason: string }): Promise<AdminUser> {
    return apiClient.patch(`/admin/users/${id}`, input);
  },

  deactivateUser(id: string, reason: string): Promise<AdminUser> {
    return apiClient.post(`/admin/users/${id}/deactivate`, { reason });
  },

  assignUserRoles(id: string, roleCodes: string[], reason: string): Promise<AdminUser> {
    return apiClient.put(`/admin/users/${id}/roles`, { roleCodes, reason });
  },

  listRoles(): Promise<PaginatedData<AdminRole>> {
    return apiClient.get('/admin/roles?pageSize=100');
  },

  getRole(id: string): Promise<AdminRole> {
    return apiClient.get(`/admin/roles/${id}`);
  },

  setRolePermissions(id: string, permissionCodes: string[], reason: string): Promise<AdminRole> {
    return apiClient.put(`/admin/roles/${id}/permissions`, { permissionCodes, reason });
  },

  listPermissions(): Promise<AdminPermission[]> {
    return apiClient.get('/admin/permissions');
  },

  listAuditLogs(filters: {
    page?: number;
    pageSize?: number;
    action?: string;
    entityType?: string;
  }): Promise<PaginatedData<AuditLog>> {
    return apiClient.get(`/admin/audit-logs${toQuery({ ...filters })}`);
  },

  getAuditLog(id: string): Promise<AuditLog> {
    return apiClient.get(`/admin/audit-logs/${id}`);
  },
};
